import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const TermsPopup = () => {
  const [open, setOpen] = useState(false);
  const [terms, setTerms] = useState(null);
  const [accepting, setAccepting] = useState(false);

  const checkTerms = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/terms/check`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.needs_acceptance) {
        const { data: latest } = await axios.get(`${API}/terms/latest`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (latest.content) {
          setTerms(latest);
          setOpen(true);
        }
      }
    } catch {
      // Not logged in or no T&C set — ignore
    }
  }, []);

  useEffect(() => {
    checkTerms();
  }, [checkTerms]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/terms/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Terms accepted');
      setOpen(false);
    } catch {
      toast.error('Failed to accept terms');
    } finally {
      setAccepting(false);
    }
  };

  if (!open || !terms) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="bg-[#2E3944] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-hidden"
        data-testid="terms-popup"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-serif text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[var(--brand)]" />
            {terms.title || 'Terms & Conditions'}
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">
            Please review and accept to continue using PropertyShop
          </DialogDescription>
        </DialogHeader>

        <div
          className="overflow-y-auto max-h-[55vh] bg-[#212A31] rounded-lg p-4 text-sm text-gray-300 leading-relaxed whitespace-pre-wrap border border-white/5"
          data-testid="terms-content"
        >
          {terms.content}
        </div>

        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-gray-500">Version {terms.version}</p>
          <Button
            onClick={handleAccept}
            disabled={accepting}
            className="bg-[var(--brand)] text-white hover:opacity-90 px-6"
            data-testid="accept-terms-btn"
          >
            {accepting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            {accepting ? 'Accepting...' : 'I Accept'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
